import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DOMPurify from 'dompurify';
import { useCreateWebhook } from '../../../hooks/useWebhooks';

interface Props {
  open: boolean;
  onClose: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  signing_secret: Yup.string().required('Signing secret is required'),
});

const WebhookForm: React.FC<Props> = ({ open, onClose }) => {
  const { mutateAsync: createWebhook, isPending } = useCreateWebhook();

  const formik = useFormik({
    initialValues: {
      name: '',
      signing_secret: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const sanitizedValues = {
        name: DOMPurify.sanitize(values.name.trim()),
        signing_secret: DOMPurify.sanitize(values.signing_secret.trim()),
      };

      await createWebhook(sanitizedValues);
      resetForm();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Add Webhook</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Webhook Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              disabled={isPending}
            />
            <TextField
              fullWidth
              id="signing_secret"
              name="signing_secret"
              label="Clockify Signing Secret"
              value={formik.values.signing_secret}
              onChange={formik.handleChange}
              error={formik.touched.signing_secret && Boolean(formik.errors.signing_secret)}
              helperText={formik.touched.signing_secret && formik.errors.signing_secret}
              disabled={isPending}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ marginBottom: 1 }}>
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            {isPending ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default WebhookForm;
